/**
 * GuideFormDialog Component
 *
 * Modal dialog for creating and editing guides.
 *
 * Features:
 * - Dynamic title in edit mode: 'Edit {guide name}'
 * - Unsaved changes detection with UnsavedChangesDialog
 * - Real-time field validation
 * - Content items management (add/remove/edit)
 * - Full ARIA accessibility
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Typography, Box, Stack, Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import {
  SDialog,
  StyledTextField,
  UnsavedChangesDialog,
  useChangeDetection,
  SortableList,
} from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import type { Guide } from '@segmently/schema-models/auth';
import { uploadGuideMedia, hasFilesToUpload } from '../utils/uploadGuideMedia';
import { ContentItemAccordion } from './ContentItemAccordion';
import type { ContentItem, FormData, FormErrors } from '../types';

export interface GuideFormDialogProps {
  /** Whether dialog is open */
  open: boolean;

  /** Dialog mode: 'create' or 'edit' */
  mode: 'create' | 'edit';

  /** Project ID for guide */
  projectId: string;

  /** Guide ID (required for edit mode) */
  guideId?: string;

  /** Initial guide data (for edit mode) */
  initialData?: Guide;

  /** Guide service instance */
  guideService: GuideService;

  /** Callback when dialog is closed */
  onClose: () => void;

  /** Callback when guide is saved successfully */
  onSave: (guide: Guide) => void;

  /** Optional ref to return focus to trigger button */
  triggerRef?: React.RefObject<HTMLElement>;
}

export const GuideFormDialog: React.FC<GuideFormDialogProps> = ({
  open,
  mode,
  projectId,
  guideId,
  initialData,
  guideService,
  onClose,
  onSave,
  triggerRef,
}) => {
  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    key: '',
    fullArticleLink: '',
    content: [],
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);

  // Change detection for unsaved changes
  const {
    startTracking,
    updateCurrent,
    isDirty,
    getChanges,
    reset,
    acceptChanges,
    revert,
  } = useChangeDetection<FormData>({
    debug: false,
  });

  // Individual field validators
  const validateName = useCallback((value: string): string | null => {
    if (!value.trim()) {
      return 'Name is required';
    }
    if (value.length > 100) {
      return 'Name must be less than 100 characters';
    }
    return null;
  }, []);

  const validateKey = useCallback((value: string): string | null => {
    if (!value.trim()) {
      return 'Key is required';
    }
    if (value.length > 100) {
      return 'Key must be less than 100 characters';
    }
    return null;
  }, []);

  // Clear field error if field becomes valid
  const clearFieldError = useCallback(
    (fieldName: keyof FormErrors, value: string) => {
      let isValid = false;

      switch (fieldName) {
        case 'name':
          isValid = validateName(value) === null;
          break;
        case 'key':
          isValid = validateKey(value) === null;
          break;
      }

      // Clear error if field becomes valid
      if (isValid) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    },
    [validateName, validateKey]
  );

  // Full form validation
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    const nameError = validateName(formData.name);
    if (nameError) newErrors.name = nameError;

    const keyError = validateKey(formData.key);
    if (keyError) newErrors.key = keyError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validateName, validateKey]);

  // Load initial data and start change tracking
  useEffect(() => {
    if (!open) {
      return;
    }

    if (mode === 'edit' && initialData) {
      // Ensure all content items have IDs
      const contentWithIds = (initialData.content || []).map((item: any) => ({
        ...item,
        id: item.id || crypto.randomUUID(),
      }));

      const editData: FormData = {
        name: initialData.name || '',
        key: initialData.key || '',
        fullArticleLink: initialData.fullArticleLink || '',
        content: contentWithIds,
      };
      setFormData(editData);
      startTracking(editData);
    } else if (mode === 'create') {
      const emptyData: FormData = {
        name: '',
        key: '',
        fullArticleLink: '',
        content: [],
      };
      setFormData(emptyData);
      startTracking(emptyData);
    }
  }, [open, mode, initialData, startTracking]);

  // Return focus to trigger when dialog closes
  const returnFocus = useCallback(() => {
    setTimeout(() => {
      if (triggerRef?.current) {
        triggerRef.current.focus();
      }
    }, 100);
  }, [triggerRef]);

  // Handle field changes with change tracking and real-time validation
  const handleFieldChange = useCallback(
    (field: keyof FormData, value: string) => {
      setFormData(prev => {
        const newData = { ...prev, [field]: value };
        updateCurrent(newData);
        return newData;
      });

      clearFieldError(field as keyof FormErrors, value);
    },
    [updateCurrent, clearFieldError]
  );

  // Handle content item field change
  const handleContentItemChange = useCallback(
    (index: number, field: keyof ContentItem, value: string) => {
      setFormData(prev => {
        const newContent = [...prev.content];
        newContent[index] = { ...newContent[index], [field]: value };
        const newData = { ...prev, content: newContent };
        updateCurrent(newData);
        return newData;
      });
    },
    [updateCurrent]
  );

  // Handle media (image/video) change - supports File objects
  const handleMediaChange = useCallback(
    (index: number, field: 'image' | 'video', value: string | File | null) => {
      setFormData(prev => {
        const newContent = [...prev.content];
        newContent[index] = { ...newContent[index], [field]: value || undefined };
        const newData = { ...prev, content: newContent };
        updateCurrent(newData);
        return newData;
      });
    },
    [updateCurrent]
  );

  // Add new content item
  const handleAddContentItem = useCallback(() => {
    setFormData(prev => {
      const newContent = [...prev.content, { id: crypto.randomUUID() }];
      const newData = { ...prev, content: newContent };
      updateCurrent(newData);
      return newData;
    });
  }, [updateCurrent]);

  // Remove content item
  const handleRemoveContentItem = useCallback(
    (index: number) => {
      setFormData(prev => {
        const newContent = prev.content.filter((_, i) => i !== index);
        const newData = { ...prev, content: newContent };
        updateCurrent(newData);
        return newData;
      });
    },
    [updateCurrent]
  );

  // Handle save
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      let savedGuide: Guide;

      // Upload media files if any exist
      let processedContent: Array<{
        id?: string;
        title?: string;
        description?: string;
        subtitle?: string;
        fullArticleLink?: string;
        image?: string;
        video?: string;
      }>;

      // For create mode with files, pre-generate ID so files are uploaded to the correct path
      let newGuideId: string | undefined;
      if (mode === 'create' && hasFilesToUpload(formData.content)) {
        newGuideId = guideService.generateId();
      }

      if (hasFilesToUpload(formData.content)) {

        // For create mode with files, use pre-generated ID
        // For edit mode, use existing guideId
        const uploadGuideId = mode === 'edit' && guideId ? guideId : newGuideId!;

        processedContent = await uploadGuideMedia(formData.content, uploadGuideId);
      } else {
        // No files to upload, use content as-is (type assertion safe here)
        processedContent = formData.content as Array<{
          id?: string;
          title?: string;
          description?: string;
          subtitle?: string;
          fullArticleLink?: string;
          image?: string;
          video?: string;
        }>;
      }

      if (mode === 'create') {
        console.log('[GuideFormDialog] Creating guide...');

        savedGuide = await guideService.create({
          id: newGuideId, // Use pre-generated ID if files were uploaded
          name: formData.name,
          key: formData.key,
          fullArticleLink: formData.fullArticleLink || undefined,
          content: processedContent.length > 0 ? processedContent : undefined,
        });
      } else {
        // Update existing guide
        if (!guideId) {
          throw new Error('Guide ID is required for edit mode');
        }

        console.log('[GuideFormDialog] Updating guide...');

        savedGuide = await guideService.update(guideId, {
          name: formData.name,
          key: formData.key,
          fullArticleLink: formData.fullArticleLink || undefined,
          content: processedContent.length > 0 ? processedContent : undefined,
        } as Partial<Guide>);
      }

      // Accept changes after successful save
      acceptChanges();

      // Call onSave callback with saved guide
      onSave(savedGuide);

      // Close dialog
      onClose();

      // Reset state
      setErrors({});
      reset();

      // Return focus to trigger
      returnFocus();
    } catch (error) {
      console.error('[GuideFormDialog] Error saving guide:', error);
      alert(`Failed to save guide: ${(error as Error)?.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateForm,
    formData,
    mode,
    guideId,
    guideService,
    onSave,
    onClose,
    acceptChanges,
    reset,
    returnFocus,
  ]);

  // Handle close with unsaved changes check
  const handleClose = useCallback(() => {
    if (isDirty()) {
      setShowUnsavedDialog(true);
      return;
    }

    // No changes, safe to close
    onClose();
    setErrors({});
    reset();
    returnFocus();
  }, [isDirty, reset, onClose, returnFocus]);

  // Handlers for UnsavedChangesDialog
  const handleSaveAndClose = useCallback(async () => {
    if (validateForm()) {
      await handleSubmit();
    }
    setShowUnsavedDialog(false);
  }, [validateForm, handleSubmit]);

  const handleDiscardAndClose = useCallback(() => {
    // Revert to original values
    const originalData = revert();
    if (originalData) {
      setFormData(originalData);
    }
    setErrors({});
    onClose();
    setShowUnsavedDialog(false);
    reset();
    returnFocus();
  }, [revert, reset, onClose, returnFocus]);

  const handleContinueEditing = useCallback(() => {
    setShowUnsavedDialog(false);
  }, []);

  // Memoized action objects to prevent re-renders
  const primaryAction = useMemo(
    () => ({
      label: isSubmitting
        ? mode === 'create'
          ? 'Creating...'
          : 'Saving...'
        : mode === 'create'
          ? 'Create'
          : 'Save',
      onClick: handleSubmit,
      disabled: isSubmitting,
      'aria-label': mode === 'create' ? 'Create new guide' : 'Save guide changes',
    }),
    [isSubmitting, mode, handleSubmit]
  );

  /**
   * Handle reorder of content items
   */
  const handleReorder = useCallback(
    (reorderedContent: ContentItem[]) => {
      setFormData((prev) => {
        const newData = { ...prev, content: reorderedContent };
        updateCurrent(newData);
        return newData;
      });
    },
    [updateCurrent]
  );

  /**
   * Generate dialog title based on mode and guide name.
   */
  const getDialogTitle = (mode: 'create' | 'edit', guideName?: string): string => {
    if (mode === 'create') {
      return 'Create Guide';
    }
    return `Edit ${guideName || 'Guide'}`;
  };

  const dialogTitle = getDialogTitle(mode, initialData?.name);

  return (
    <>
      {/* Main Form Dialog */}
      <SDialog
        primaryAction={primaryAction}
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        title={dialogTitle}
        aria-labelledby="guide-form-title"
        aria-describedby="guide-form-description"
      >
        <Stack spacing={3}>
          {/* Clear description for context */}
          <Typography
            id="guide-form-description"
            variant="body2"
            color="text.secondary"
          >
            {mode === 'create'
              ? 'Create a new guide by filling in the required information below.'
              : 'Update the guide information below. Required fields are marked with *.'}
          </Typography>

          {/* Flat layout without unnecessary SAccordion groupings */}
          <StyledTextField
            label="Name"
            value={formData.name}
            onChange={(value) => handleFieldChange('name', value)}
            error={Boolean(errors.name)}
            errorMessage={errors.name}
            required
            fullWidth
            maxLength={100}
          />

          <StyledTextField
            label="Key"
            value={formData.key}
            onChange={(value) => handleFieldChange('key', value)}
            error={Boolean(errors.key)}
            errorMessage={errors.key}
            required
            fullWidth
            maxLength={100}
            helperText="Unique identifier for this guide"
          />

          <StyledTextField
            label="Full Article Link"
            value={formData.fullArticleLink}
            onChange={(value) => handleFieldChange('fullArticleLink', value)}
            fullWidth
            placeholder="https://example.com/article"
          />

          {/* Content Items Section - Draggable Accordions */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Content Items
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add content items to structure your guide content. Drag items to reorder them.
            </Typography>

            <SortableList items={formData.content} onReorder={handleReorder}>
              {(item, index) => (
                <SortableList.Item>
                  <ContentItemAccordion
                    item={item}
                    index={index}
                    onFieldChange={handleContentItemChange}
                    onMediaChange={handleMediaChange}
                    onRemove={handleRemoveContentItem}
                  />
                </SortableList.Item>
              )}
            </SortableList>

            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddContentItem}
              sx={{ textTransform: 'none', mt: 2 }}
            >
              Add Content Item
            </Button>

            {formData.content.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                No content items added yet. Click "Add Content Item" to get started.
              </Typography>
            )}
          </Box>
        </Stack>
      </SDialog>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={showUnsavedDialog}
        changedFields={getChanges()}
        onSave={handleSaveAndClose}
        onDiscard={handleDiscardAndClose}
        onCancel={handleContinueEditing}
        saving={isSubmitting}
        title="Unsaved Changes Detected"
        message="You have made changes to the guide form. What would you like to do with these changes?"
        saveButtonText="Save & Close"
        discardButtonText="Discard Changes"
        cancelButtonText="Continue Editing"
      />
    </>
  );
};
